import React, { useCallback, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { invoiceContract } from "@common/contracts/invoice.contract";
import { billContract } from "@common/contracts/bill.contract";
import { AccrualReviewApproveResponse } from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { Drawer } from "@design-system/Drawer/Drawer";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { Loader } from "@design-system/Loader/Loader";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { useBillAccrualMatches } from "@api/bill.api";
import { useInvoiceAccrualMatches } from "@api/invoice.api";
import { useScope } from "@state/scopeContext.lib";
import {
    applyApproveToAccrualReviewCache,
    DOCUMENT_MATCH_BILL_QUERY_KEY,
    DOCUMENT_MATCH_INVOICE_QUERY_KEY,
    invalidateAccrualMatchCaches,
} from "@components/accounting/accrualReview/accrualReviewCache";
import { AccrualMatchProvider } from "./AccrualMatchContext";
import { DocumentMatchPage } from "./DocumentMatchPage";
import { DocumentEditAllocationContainer } from "./DocumentEditAllocationContainer";
import { MultiTransactionAllocationDrawer } from "./MultiTransactionAllocationDrawer";
import {
    accrualMatchedTransactionToMatchableItem,
    billToMatchableItem,
    invoiceToMatchableItem,
    parseTransactionMatchId,
} from "./mappers";
import { MatchableItem } from "./types";
import styles from "./TransactionMatchDrawer.module.scss";

const invoiceClient = getBackendAPIClient(invoiceContract, undefined, {
    jsonQuery: true,
});
const billClient = getBackendAPIClient(billContract, undefined, {
    jsonQuery: true,
});

interface DocumentMatchDrawerProps {
    documentType: "bill" | "invoice";
    documentId: string;
    open: boolean;
    onClose: () => void;
}

export const DocumentMatchDrawer: React.FC<DocumentMatchDrawerProps> = ({
    documentType,
    documentId,
    open,
    onClose,
}) => {
    const { workspaceId } = useScope();

    const invoiceQuery = useQuery({
        queryKey: [DOCUMENT_MATCH_INVOICE_QUERY_KEY, workspaceId, documentId],
        queryFn: async () => {
            const result = await invoiceClient.getInvoices({
                params: { workspaceId: workspaceId! },
                query: { filters: { ids: [documentId] }, page: 0, limit: 1 },
            });
            return result.body.data[0] ?? null;
        },
        enabled: open && !!workspaceId && documentType === "invoice",
    });

    const billQuery = useQuery({
        queryKey: [DOCUMENT_MATCH_BILL_QUERY_KEY, workspaceId, documentId],
        queryFn: async () => {
            const result = await billClient.getBills({
                params: { workspaceId: workspaceId! },
                query: { filters: { ids: [documentId] }, page: 0, limit: 1 },
            });
            return result.body.data[0] ?? null;
        },
        enabled: open && !!workspaceId && documentType === "bill",
    });

    const billMatchesQuery = useBillAccrualMatches(
        documentType === "bill" && open ? documentId : undefined,
    );
    const invoiceMatchesQuery = useInvoiceAccrualMatches(
        documentType === "invoice" && open ? documentId : undefined,
    );

    const existingMatches = useMemo<MatchableItem[]>(() => {
        const matches =
            documentType === "bill"
                ? (billMatchesQuery.data ?? [])
                : (invoiceMatchesQuery.data ?? []);
        return matches.map(accrualMatchedTransactionToMatchableItem);
    }, [documentType, billMatchesQuery.data, invoiceMatchesQuery.data]);

    const sourceItem = useMemo<MatchableItem | null>(() => {
        if (documentType === "invoice" && invoiceQuery.data) {
            return invoiceToMatchableItem(invoiceQuery.data);
        }
        if (documentType === "bill" && billQuery.data) {
            return billToMatchableItem(billQuery.data);
        }
        return null;
    }, [documentType, invoiceQuery.data, billQuery.data]);

    const matchesQuery =
        documentType === "bill" ? billMatchesQuery : invoiceMatchesQuery;
    const isMatchesQueryLoading =
        matchesQuery.isLoading ||
        (matchesQuery.isFetching && !matchesQuery.isFetchedAfterMount);
    const isLoading =
        (documentType === "invoice"
            ? invoiceQuery.isLoading
            : billQuery.isLoading) || isMatchesQueryLoading;
    const isError =
        documentType === "invoice" ? invoiceQuery.isError : billQuery.isError;

    if (sourceItem && !isMatchesQueryLoading) {
        return (
            <DocumentMatchDrawerContent
                documentType={documentType}
                documentId={documentId}
                sourceItem={sourceItem}
                initialSelectedItems={existingMatches}
                open={open}
                onClose={onClose}
            />
        );
    }

    return (
        <Drawer
            open={open}
            onClose={onClose}
            size="fullScreen"
            noPadding
            headerAddonLeft={
                <span className={styles.drawerTitle}>Find the match</span>
            }
        >
            {isLoading && <Loader />}
            {isError && (
                <EmptyState
                    header={`${documentType === "invoice" ? "Invoice" : "Bill"} not found`}
                />
            )}
        </Drawer>
    );
};

interface ContentProps {
    documentType: "bill" | "invoice";
    documentId: string;
    sourceItem: MatchableItem;
    initialSelectedItems: MatchableItem[];
    open: boolean;
    onClose: () => void;
}

const DocumentMatchDrawerContent: React.FC<ContentProps> = ({
    documentType,
    documentId,
    sourceItem,
    initialSelectedItems,
    open,
    onClose,
}) => {
    const queryClient = useQueryClient();
    const { enabled: isGlFirst } = useIsGlFirst();
    const originalItems = useRef(initialSelectedItems).current;
    const [editAllocationTxId, setEditAllocationTxId] = useState<number | null>(
        null,
    );

    const [multiEditTransactionIds, setMultiEditTransactionIds] = useState<
        number[] | null
    >(null);

    const handleOpenEditAllAllocations = useCallback(
        (items: MatchableItem[]) =>
            setMultiEditTransactionIds(
                items.map((item) => parseTransactionMatchId(item.id)),
            ),
        [],
    );

    const handleCloseMultiEdit = useCallback(
        () => setMultiEditTransactionIds(null),
        [],
    );

    const handleCloseAfterMultiSave = useCallback(() => {
        setMultiEditTransactionIds(null);
        onClose();
    }, [onClose]);

    const handleOpenEditAllocation = useCallback((item: MatchableItem) => {
        setEditAllocationTxId(parseTransactionMatchId(item.id));
    }, []);

    const handleCloseEditAllocation = useCallback(() => {
        setEditAllocationTxId(null);
    }, []);

    const handleEditAllocationSaveSuccess = useCallback(
        (response: AccrualReviewApproveResponse) => {
            if (editAllocationTxId == null) {
                return;
            }
            applyApproveToAccrualReviewCache(editAllocationTxId, response);
            invalidateAccrualMatchCaches(queryClient, {
                transactionId: editAllocationTxId,
            });
            setEditAllocationTxId(null);
            onClose();
        },
        [editAllocationTxId, queryClient, onClose],
    );

    return (
        <>
            <AccrualMatchProvider
                sourceTransaction={null}
                initialSelectedItems={originalItems}
                isGlFirst={isGlFirst}
                onOpenEditAllocation={handleOpenEditAllocation}
                onClose={onClose}
            >
                <Drawer
                    open={open}
                    onClose={onClose}
                    size="fullScreen"
                    noPadding
                    headerAddonLeft={
                        <span className={styles.drawerTitle}>
                            Find the match
                        </span>
                    }
                >
                    <DocumentMatchPage
                        documentType={documentType}
                        documentId={documentId}
                        sourceItem={sourceItem}
                        originalItems={originalItems}
                        onEditAllAllocations={handleOpenEditAllAllocations}
                    />
                </Drawer>
            </AccrualMatchProvider>

            {editAllocationTxId !== null && (
                <DocumentEditAllocationContainer
                    transactionId={editAllocationTxId}
                    documentSourceItem={sourceItem}
                    onClose={handleCloseEditAllocation}
                    onSaveSuccess={handleEditAllocationSaveSuccess}
                />
            )}

            {multiEditTransactionIds && (
                <MultiTransactionAllocationDrawer
                    open
                    onClose={handleCloseMultiEdit}
                    documentId={documentId}
                    documentItem={sourceItem}
                    transactionIds={multiEditTransactionIds}
                    onCloseAfterSave={handleCloseAfterMultiSave}
                />
            )}
        </>
    );
};
